import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import PublicHeader from "@/components/PublicHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useSeo, ORG_LD, SITE } from "@/lib/seo";
import { usePlaces } from "@/pages/AreaJobs";

export function EmbedWidget() {
  const { place } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/public/area/${place}`).then((r) => setD(r.data)).catch(() => setD({ jobs: [] })); }, [place]);
  return (
    <div className="bg-white p-3 font-sans text-sm" data-testid="embed-widget">
      <p className="mb-2 font-semibold">Latest jobs{d?.place ? ` in ${d.place.name}` : ""}</p>
      <ul className="space-y-2">{(d?.jobs || []).slice(0, 8).map((j) => (
        <li key={j.job_id}><a href={`${SITE}/job/${j.job_id}`} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-700 hover:underline">{j.title}</a>
          <span className="block text-xs text-slate-500">{j.employer} · {(j.location || "").slice(0, 50)}</span></li>))}</ul>
      <a href={`${SITE}/jobs/in/${place}`} target="_blank" rel="noopener noreferrer" className="mt-3 block text-xs text-slate-500">Jobs by <span className="font-semibold">JobPedestal</span></a>
    </div>
  );
}

export default function Partners() {
  const places = usePlaces();
  const [place, setPlace] = useState(() => new URLSearchParams(window.location.search).get("place") || "united-states");
  useSeo({ title: "Free job widget and link badges for your website", path: "/partners",
    description: "Add a free, always-current jobs widget for your city, state or province to your website, blog or community page.", jsonLd: [ORG_LD] });
  const widget = `<iframe src="${SITE}/embed/${place}" width="100%" height="420" style="border:1px solid #e2e8f0;border-radius:12px" title="Jobs by JobPedestal" loading="lazy"></iframe>\n<a href="${SITE}/jobs/in/${place}">Find more jobs on JobPedestal</a>`;
  const badge = `<a href="${SITE}" title="Verified jobs in the US & Canada"><img src="${SITE}/logo192.png" width="40" height="40" alt="JobPedestal"> Find verified jobs on JobPedestal</a>`;
  const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copied"));
  const opts = places ? [...places.places, ...places.cities] : [];
  useEffect(() => {
    if (opts.length && !opts.some((p) => p.slug === place)) { setPlace("united-states"); }
  }, [opts, place]);
  return (
    <div className="min-h-screen" data-testid="partners-page">
      <PublicHeader />
      <main className="mx-auto max-w-4xl px-4 py-12 sm:px-8">
        <p className="eyebrow mb-3">For schools, workforce boards, community groups and bloggers</p>
        <h1 className="text-4xl font-extrabold sm:text-5xl">Free local jobs widget</h1>
        <p className="mt-4 max-w-2xl text-slate-600">Show visitors the newest verified jobs in your city, state or province. It updates itself every day, costs nothing and needs no account.</p>
        <label className="mt-8 block text-sm font-medium">Area
          <select className="mt-1 block w-full rounded-xl border bg-white p-2.5 sm:w-80" value={place} onChange={(e) => setPlace(e.target.value)} data-testid="partners-place-select">
            {opts.map((p) => <option key={p.slug} value={p.slug}>{p.kind === "city" ? `${p.name}, ${p.state}` : p.name}</option>)}</select></label>
        <pre className="mt-4 overflow-x-auto whitespace-pre-wrap rounded-xl bg-slate-950 p-4 text-xs text-slate-100" data-testid="partners-widget-code">{widget}</pre>
        <Button className="mt-3 rounded-full" onClick={() => copy(widget)} data-testid="partners-copy-widget">Copy widget code</Button>
        <iframe src={`/embed/${place}`} title="Widget preview" className="mt-6 h-[420px] w-full rounded-xl border" data-testid="partners-preview" />
        <h2 className="mt-12 text-2xl font-bold">Link badge</h2>
        <p className="mt-2 text-slate-600">Recommend JobPedestal on your careers, resources or alumni page.</p>
        <pre className="mt-4 overflow-x-auto whitespace-pre-wrap rounded-xl bg-slate-950 p-4 text-xs text-slate-100" data-testid="partners-badge-code">{badge}</pre>
        <Button variant="outline" className="mt-3 rounded-full" onClick={() => copy(badge)} data-testid="partners-copy-badge">Copy badge code</Button>
      </main>
      <SiteFooter />
    </div>
  );
}
